export const REGISTRATION_STORAGE_KEY = 'registration';

/**
 * Signup flow values stored in localStorage under REGISTRATION_STORAGE_KEY.
 * Canonical source of truth — consumed by both @fly/features/sign-up and
 * @fly/features/download (which can't import from sign-up directly).
 */
export const SignupFlow = {
  ACCEPT_INVITATION: 'accept-invitation',
  TEAM_REGISTER: 'team_register',
  REGISTER: 'register',
  SIGNUP: 'signup',
} as const;
export type SignupFlow = (typeof SignupFlow)[keyof typeof SignupFlow];

export const VAT_COUNTRY_REGEX_MAP: Record<string, string> = {
  belgium: '^BE[01][0-9]{9}$',
  france: '^FR[0-9A-Z]{2}[0-9]{9}$',
  israel: '^[0-9]{9}$',
  luxembourg: '^LU[0-9]{8}$',
  'united kingdom': '^GB([0-9]{9}|[0-9]{12}|GD[0-4][0-9]{2}|HA[5-9][0-9]{2})$',
} as const;
export const FILTER_PROMPT_VALUES_KEY = 'prompt_values';
export const FILTER_PROMPT_QUERY_KEY = 'prompt_query';
/** f.prompt_trigger_text — FlyFilterByPrompt trigger button label (optional deep link). */
export const FILTER_PROMPT_TRIGGER_TEXT_KEY = 'prompt_trigger_text';
/** f.prompt_header_text — FlyFilterByPrompt dropdown header label (optional deep link). */
export const FILTER_PROMPT_HEADER_TEXT_KEY = 'prompt_header_text';
/** f.prompt_deploy_count — release-delta wave size for locale-safe prompt UI (optional deep link). */
export const FILTER_PROMPT_DEPLOY_COUNT_KEY = 'prompt_deploy_count';
/** f.prompt_deploy_rollback — "1" when the release-delta link is for a rollback wave. */
export const FILTER_PROMPT_DEPLOY_ROLLBACK_KEY = 'prompt_deploy_rollback';

/** URL query param prefix for table filters (e.g. f.severity=high,medium). */
export const FILTER_PARAM_PREFIX = 'f.';

/** Delimiter for multi-value filter in URL (e.g. f.severity=high,medium,low). */
export const MULTI_VALUE_FILTER_DELIMITER = ',';

export const TIME_SLOT_OPTIONS: {
  value: string;
  labelKey: string;
  defaultLabel: string;
  msAgo: number;
}[] = [
  {
    value: '24h',
    labelKey: 'app.dashboard.filters.time_slot.last_24_hours',
    defaultLabel: 'Last 24 hours',
    msAgo: 24 * 60 * 60 * 1000,
  },
  {
    value: '7d',
    labelKey: 'app.dashboard.filters.time_slot.last_7_days',
    defaultLabel: 'Last 7 days',
    msAgo: 7 * 24 * 60 * 60 * 1000,
  },
  {
    value: '14d',
    labelKey: 'app.dashboard.filters.time_slot.last_14_days',
    defaultLabel: 'Last 14 Days',
    msAgo: 14 * 24 * 60 * 60 * 1000,
  },
];
export const DISPLAY_DATE_FORMAT = 'MMM d, yyyy';

export const PACKAGE_MANAGERS = {
  AGENTPACKAGES: 'agentpackages',
  DOCKER: 'docker',
  GENERIC: 'generic',
  GO: 'go',
  HELM: 'helm',
  MAVEN: 'maven',
  NPM: 'npm',
  NUGET: 'nuget',
  OCI: 'oci',
  PYPI: 'pypi',
} as const;

/** Package types with a display label that isn't a simple capitalized version of the value. */
const PACKAGE_MANAGER_LABEL_OVERRIDES: Record<string, string> = {
  oci: 'OCI',
  agentpackages: 'Agent Packages',
};

export const PACKAGE_MANAGER_OPTIONS: { value: string; label: string }[] =
  Object.values(PACKAGE_MANAGERS).map((value) => ({
    value: value.toLowerCase(),
    label:
      PACKAGE_MANAGER_LABEL_OVERRIDES[value] ??
      value.charAt(0).toUpperCase() + value.slice(1),
  }));
